/**
 * BrokerSection Component
 *
 * Single-broker section presented as a "button + capture modal", mirroring
 * the Collateral Providers section for visual consistency:
 *
 *  - When no broker is selected the section header shows "Broker (0)" and
 *    an "Add Broker" button; below is an empty-state Paper.
 *  - When a broker is selected the header shows "Broker (1)", a single-row
 *    table renders the broker / fees / stamp duty with edit + remove
 *    actions. The Add button is hidden (single-broker constraint).
 *
 * The form fields broker_id / broker_fees / stamp_duty / stamp_duty_fees
 * remain authoritative — the modal just writes into them via setValue.
 * is_brokered is kept in sync by an effect on broker_id presence so the
 * existing validation in useContractValidation.js continues to apply.
 *
 * Fee auto-population (commission rates → placement fees) still runs on
 * broker_id change so picking a broker in the modal pre-fills the fee
 * field exactly as before.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import StakeholderAutocomplete from '../../../common/StakeholderAutocomplete';
import ConfirmDialog from '../../../common/ConfirmDialog';

// Find the best matching commission rate using priority tiers
function findMatchingCommissionRate(commissionRates, brokerId, investorId, capitalAmount) {
  const today = new Date().toISOString().split('T')[0];

  const validRates = commissionRates.filter(cr =>
    cr.active !== false &&
    cr.effective_from <= today &&
    (!cr.effective_to || cr.effective_to >= today)
  );

  const tiers = [
    validRates.filter(cr => cr.broker_id === brokerId && cr.investor_id === investorId),
    validRates.filter(cr => cr.broker_id === brokerId && cr.investor_id === null),
    validRates.filter(cr => cr.broker_id === null && cr.investor_id === investorId),
    validRates.filter(cr => cr.broker_id === null && cr.investor_id === null),
  ];

  for (const tier of tiers) {
    if (tier.length === 0) continue;
    const amt = parseFloat(capitalAmount) || 0;
    const amountMatch = tier.find(cr => {
      if (cr.min_amount && amt < parseFloat(cr.min_amount)) return false;
      if (cr.max_amount && amt > parseFloat(cr.max_amount)) return false;
      return true;
    });
    if (amountMatch) return amountMatch;
    const noAmountConstraint = tier.find(cr => !cr.min_amount && !cr.max_amount);
    if (noAmountConstraint) return noAmountConstraint;
  }
  return null;
}

const EMPTY_FORM = {
  broker_id: '',
  broker_fees: '',
  stamp_duty: false,
  stamp_duty_fees: '',
};

function BrokerSection() {
  const { watch, setValue } = useFormContext();
  const { refData, appendToRefData, isEdit, initialData, isEditable } = useContractFormContext();

  const brokerId = watch('broker_id');
  const brokerFees = watch('broker_fees');
  const stampDuty = watch('stamp_duty');
  const stampDutyFees = watch('stamp_duty_fees');
  const isBrokered = !!brokerId;
  const disabled = isEditable === false;

  // Sync is_brokered with broker_id presence. Validation in
  // useContractValidation.js still keys off is_brokered.
  useEffect(() => {
    setValue('is_brokered', !!brokerId);
  }, [brokerId, setValue]);

  const productId = watch('product_id');
  const investmentTerm = watch('investment_term');
  const investmentTermValue = watch('investment_term_value');
  const investorId = watch('investor_id');
  const capitalAmount = watch('capital_amount');

  const [initialBrokerLoaded, setInitialBrokerLoaded] = useState(false);
  const [feeSource, setFeeSource] = useState(null);

  // Fee auto-population on broker_id change (unchanged behaviour)
  useEffect(() => {
    if (!initialBrokerLoaded && isEdit && initialData?.is_brokered) {
      setInitialBrokerLoaded(true);
      return;
    }

    if (!isBrokered) return;

    if (brokerId && refData.commissionRates.length > 0) {
      const match = findMatchingCommissionRate(
        refData.commissionRates, brokerId, investorId || null, capitalAmount
      );
      if (match) {
        setValue('broker_fees', match.rate);
        setFeeSource('commission');
        setInitialBrokerLoaded(true);
        return;
      }
    }

    if (!productId || !investmentTermValue || refData.placementFees.length === 0) {
      setInitialBrokerLoaded(true);
      return;
    }

    const termValue = parseInt(investmentTermValue) || 0;
    let termYears = termValue;
    if (investmentTerm === 'Months') termYears = termValue / 12;
    else if (investmentTerm === 'Days') termYears = termValue / 365;

    const selectedProduct = refData.products.find(p => p.id === productId);
    if (!selectedProduct) return;

    const matchingBrokerFee = refData.placementFees.find(fee =>
      fee.type === 'Broker Fee' &&
      fee.active !== false &&
      fee.applicable_products?.split(',').map(p => p.trim()).includes(selectedProduct.type) &&
      termYears >= (fee.min_term_years || 0) &&
      termYears <= (fee.max_term_years || 999)
    );

    if (matchingBrokerFee) {
      setValue('broker_fees', matchingBrokerFee.percentage);
      setFeeSource('placement');
    }

    const matchingStampDuty = refData.placementFees.find(fee =>
      fee.type === 'Stamp Duty' &&
      fee.active !== false &&
      fee.applicable_products?.split(',').map(p => p.trim()).includes(selectedProduct.type) &&
      termYears >= (fee.min_term_years || 0) &&
      termYears <= (fee.max_term_years || 999)
    );

    if (matchingStampDuty) {
      setValue('stamp_duty', true);
      setValue('stamp_duty_fees', matchingStampDuty.percentage);
    }

    setInitialBrokerLoaded(true);
  }, [isBrokered, brokerId, investorId, capitalAmount, productId, investmentTerm, investmentTermValue, refData.commissionRates, refData.placementFees, refData.products, setValue, isEdit, initialData, initialBrokerLoaded]);

  // Dialog state — separate from the live form so cancel doesn't mutate it.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FORM });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  const handleOpenDialog = (mode) => {
    if (mode === 'edit') {
      setDraft({
        broker_id: brokerId || '',
        broker_fees: brokerFees ?? '',
        stamp_duty: !!stampDuty,
        stamp_duty_fees: stampDutyFees ?? '',
      });
    } else {
      setDraft({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDraft({ ...EMPTY_FORM });
  };

  const isSubmitDisabled = () => {
    if (!draft.broker_id) return true;
    const fees = parseFloat(draft.broker_fees);
    if (!isFinite(fees) || fees < 0) return true;
    if (draft.stamp_duty) {
      const sd = parseFloat(draft.stamp_duty_fees);
      if (!isFinite(sd) || sd < 0) return true;
    }
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitDisabled()) return;
    setValue('broker_id', parseInt(draft.broker_id, 10));
    setValue('broker_fees', parseFloat(draft.broker_fees));
    setValue('stamp_duty', !!draft.stamp_duty);
    setValue('stamp_duty_fees', draft.stamp_duty
      ? (draft.stamp_duty_fees === '' || draft.stamp_duty_fees === null ? '' : parseFloat(draft.stamp_duty_fees))
      : '');
    handleCloseDialog();
  };

  const handleRemove = () => {
    setConfirmDialog({
      open: true,
      onConfirm: () => {
        setValue('broker_id', '');
        setValue('broker_fees', '');
        setValue('stamp_duty', false);
        setValue('stamp_duty_fees', '');
        setConfirmDialog({ open: false, onConfirm: null });
      },
    });
  };

  const brokerName = (refData.brokers || []).find(b => b.id === brokerId)?.name
    || initialData?.broker_name
    || '—';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Broker ({isBrokered ? 1 : 0})
          </Typography>
        </Box>
        {!disabled && !isBrokered && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Add Broker
          </Button>
        )}
      </Box>

      {!isBrokered ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography color="textSecondary">
            No broker on this contract. Click "Add Broker" to record one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Broker</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Broker Fees</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Stamp Duty</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Stamp Duty Fees</TableCell>
                {!disabled && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium">{brokerName}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {brokerFees !== '' && brokerFees != null ? `${parseFloat(brokerFees).toFixed(2)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={stampDuty ? 'Yes' : 'No'}
                    size="small"
                    color={stampDuty ? 'info' : 'default'}
                    variant={stampDuty ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {stampDuty && stampDutyFees !== '' && stampDutyFees != null
                      ? `${parseFloat(stampDutyFees).toFixed(2)}%`
                      : '—'}
                  </Typography>
                </TableCell>
                {!disabled && (
                  <TableCell align="center">
                    <Tooltip title="Edit broker">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove broker">
                      <IconButton size="small" color="error" onClick={handleRemove}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{brokerId ? 'Edit Broker' : 'Add Broker'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={12}>
              <StakeholderAutocomplete
                stakeholderType="broker"
                options={refData.brokers}
                value={draft.broker_id}
                onChange={(id) => setDraft(prev => ({ ...prev, broker_id: id }))}
                onStakeholderCreated={(newBroker) => {
                  appendToRefData('brokers', newBroker);
                  setDraft(prev => ({ ...prev, broker_id: newBroker.id }));
                }}
                label="Broker *"
                countries={refData.currencies}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="Broker Fees (%) *"
                type="number"
                fullWidth
                value={draft.broker_fees}
                onChange={(e) => setDraft(prev => ({ ...prev, broker_fees: e.target.value }))}
                inputProps={{ step: 0.01, min: 0 }}
                helperText={feeSource === 'commission' ? 'Auto-populated from commission rates' : 'Auto-populated from reference data when available'}
              />
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.stamp_duty}
                    onChange={(e) => setDraft(prev => ({
                      ...prev,
                      stamp_duty: e.target.checked,
                      stamp_duty_fees: e.target.checked ? prev.stamp_duty_fees : '',
                    }))}
                  />
                }
                label="Stamp Duty Applicable"
              />
            </Grid>

            {draft.stamp_duty && (
              <Grid size={12}>
                <TextField
                  label="Stamp Duty (%) *"
                  type="number"
                  fullWidth
                  value={draft.stamp_duty_fees}
                  onChange={(e) => setDraft(prev => ({ ...prev, stamp_duty_fees: e.target.value }))}
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText="Auto-populated from reference data when available"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled()}>
            {isBrokered ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove Broker"
        message="Are you sure you want to remove the broker? Broker fees and stamp duty fields will be cleared."
        confirmText="Remove"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, onConfirm: null })}
      />
    </Box>
  );
}

export default BrokerSection;
